// Phase 10A.5 — Fair Price-tilted projection over the curated
// public-read signal_cards boundary.
// Phase 10A.6 — Joined fixture labels via the existing fixture_cache
// safe-read pattern (same join useWatchlistItems uses). No new RLS,
// no new RPC, no new boundary.
//
// This hook does NOT touch fair_price_predictions, signal_card_grades,
// daily_intelligence_ledger, fixture_market_closing_lines,
// fixture_market_odds_history, or market_base_rates. It re-uses
// usePublicSignalCards (RLS gated by visibility='public' AND
// signal_status='ready') and re-projects with Fair Price-leaning
// ordering: positive value_edge first, then neutral, then negative —
// negative gaps are NOT framed as opportunities.

import { useEffect, useMemo, useState } from "react";
import { getBrowserClient } from "../supabase/browser.ts";
import {
  PUBLIC_SIGNAL_CARDS_DEFAULT_LIMIT,
  usePublicSignalCards,
  type PublicSignalCardViewModel,
} from "./usePublicSignalCards.ts";

export type FairPriceTone = "positive" | "neutral" | "negative";

export interface FairPriceCardViewModel extends PublicSignalCardViewModel {
  readonly tone: FairPriceTone;
  readonly fixture_label: {
    readonly home_team: string | null;
    readonly away_team: string | null;
    readonly league_name: string | null;
    readonly kickoff_at: string | null;
  } | null;
}

export interface FairPriceCardsResult {
  readonly cards: readonly FairPriceCardViewModel[];
  readonly loading: boolean;
  readonly error: string | null;
}

const POSITIVE_VALUE_EDGE_THRESHOLD = 0;

function deriveTone(value_edge: number | null): FairPriceTone {
  if (value_edge === null || !Number.isFinite(value_edge)) return "neutral";
  if (value_edge > POSITIVE_VALUE_EDGE_THRESHOLD) return "positive";
  if (value_edge < POSITIVE_VALUE_EDGE_THRESHOLD) return "negative";
  return "neutral";
}

function rank(tone: FairPriceTone): number {
  switch (tone) {
    case "positive":
      return 0;
    case "neutral":
      return 1;
    case "negative":
      return 2;
  }
}

interface FixtureRow {
  fixture_id: string;
  home_team: string | null;
  away_team: string | null;
  league_name: string | null;
  kickoff_at: string | null;
}

export function useFairPriceCards(args: { limit?: number } = {}): FairPriceCardsResult {
  const limit = args.limit ?? PUBLIC_SIGNAL_CARDS_DEFAULT_LIMIT;
  const result = usePublicSignalCards({ limit });

  const [fixtureMap, setFixtureMap] = useState<ReadonlyMap<string, FixtureRow>>(new Map());

  // Phase 10A.6 — fetch fixture labels for the current card set. Same
  // pattern useWatchlistItems uses; fixture_cache is authenticated-
  // readable via the existing fixture_cache_select_authenticated RLS
  // policy. No new boundary, no schema change.
  useEffect(() => {
    let cancelled = false;
    const fixtureIds = Array.from(new Set(result.cards.map((c) => c.fixture_id))).filter(
      (id) => typeof id === "string" && id.length > 0,
    );
    if (fixtureIds.length === 0) {
      setFixtureMap(new Map());
      return () => {
        cancelled = true;
      };
    }
    const client = getBrowserClient();
    void (async () => {
      const resp = await client
        .from("fixture_cache")
        .select("fixture_id, home_team, away_team, kickoff_at, league_name")
        .in("fixture_id", fixtureIds);
      if (cancelled) return;
      const next = new Map<string, FixtureRow>();
      for (const row of (resp.data ?? []) as FixtureRow[]) {
        next.set(row.fixture_id, row);
      }
      setFixtureMap(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [result.cards]);

  const cards = useMemo<readonly FairPriceCardViewModel[]>(() => {
    const projected: FairPriceCardViewModel[] = result.cards.map((c) => {
      const fix = fixtureMap.get(c.fixture_id);
      return {
        ...c,
        tone: deriveTone(c.value_edge),
        fixture_label:
          fix !== undefined
            ? {
                home_team: fix.home_team,
                away_team: fix.away_team,
                league_name: fix.league_name,
                kickoff_at: fix.kickoff_at,
              }
            : null,
      };
    });
    return projected.sort((a, b) => {
      const r = rank(a.tone) - rank(b.tone);
      if (r !== 0) return r;
      const av = a.value_edge ?? 0;
      const bv = b.value_edge ?? 0;
      return bv - av;
    });
  }, [result.cards, fixtureMap]);

  return useMemo(
    () => ({ cards, loading: result.loading, error: result.error }),
    [cards, result.loading, result.error],
  );
}
